import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { propertyProfileApi } from "../services/api";
import ModuleAskBar from "../components/shared/ModuleAskBar";
import SheetHandle from "../components/shared/SheetHandle";
import ConfirmModal from "../components/shared/ConfirmModal";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";
import "./PropertyProfilePage.css";

const EMPTY_SPACE = {
  name: "",
  space_type: "room",
  floor_level: "",
  length_ft: "",
  width_ft: "",
  paint_color: "",
  flooring: "",
  safety_notes: "",
};
const EMPTY_POLICY = {
  provider_name: "",
  policy_number: "",
  coverage_type: "homeowner",
  monthly_premium: "",
  deductible: "",
  dwelling_coverage: "",
  personal_property_coverage: "",
  liability_coverage: "",
  effective_date: "",
  policy_expiry: "",
  agent_contact: "",
  notes: "",
};
const EMPTY_CLAIM = {
  policy_id: "",
  incident_date: new Date().toLocaleDateString("en-CA"),
  claim_type: "property_damage",
  description: "",
  claim_number: "",
  amount_claimed: "",
  amount_paid: "",
  status: "open",
  notes: "",
};
const HOME_TYPE_SUGGESTIONS = [
  "Single-family",
  "Townhome",
  "Condo",
  "Apartment",
  "Manufactured home",
  "Duplex",
  "Co-op",
  "Farmhouse",
];
const FOUNDATION_TYPE_SUGGESTIONS = [
  "Slab",
  "Crawl space",
  "Full basement",
  "Partial basement",
  "Pier and beam",
];
const ROOF_TYPE_SUGGESTIONS = [
  "Asphalt shingle",
  "Metal",
  "Tile",
  "Slate",
  "Flat/membrane",
  "Wood shake",
];
const EXTERIOR_MATERIAL_SUGGESTIONS = [
  "Vinyl siding",
  "Brick",
  "Stucco",
  "Wood siding",
  "Fiber cement",
  "Stone",
];
const HEATING_TYPE_SUGGESTIONS = [
  "Forced air (gas)",
  "Forced air (electric)",
  "Heat pump",
  "Radiant/baseboard",
  "Boiler",
  "Geothermal",
];
const COOLING_TYPE_SUGGESTIONS = [
  "Central air",
  "Heat pump",
  "Window units",
  "Ductless mini-split",
  "None",
];
const WATER_SOURCE_SUGGESTIONS = ["Municipal", "Private well", "Shared well"];
const SEWER_TYPE_SUGGESTIONS = ["Municipal sewer", "Septic system"];
const FLOORING_SUGGESTIONS = [
  "Hardwood",
  "Carpet",
  "Tile",
  "Laminate",
  "Vinyl/LVP",
  "Concrete",
];
const FLOOR_LEVEL_SUGGESTIONS = [
  "Basement",
  "Main floor",
  "Second floor",
  "Third floor",
  "Attic",
];
const US_STATE_SUGGESTIONS = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];
const PROFILE_DATALISTS = [
  ["hm-pp-home-type", HOME_TYPE_SUGGESTIONS],
  ["hm-pp-foundation-type", FOUNDATION_TYPE_SUGGESTIONS],
  ["hm-pp-roof-type", ROOF_TYPE_SUGGESTIONS],
  ["hm-pp-exterior-material", EXTERIOR_MATERIAL_SUGGESTIONS],
  ["hm-pp-heating-type", HEATING_TYPE_SUGGESTIONS],
  ["hm-pp-cooling-type", COOLING_TYPE_SUGGESTIONS],
  ["hm-pp-water-source", WATER_SOURCE_SUGGESTIONS],
  ["hm-pp-sewer-type", SEWER_TYPE_SUGGESTIONS],
  ["hm-pp-flooring", FLOORING_SUGGESTIONS],
  ["hm-pp-floor-level", FLOOR_LEVEL_SUGGESTIONS],
  ["hm-pp-state", US_STATE_SUGGESTIONS],
];

export default function PropertyProfilePage() {
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState({});
  const [space, setSpace] = useState(EMPTY_SPACE);
  const [editing, setEditing] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [spaceOpen, setSpaceOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false),
    [claimOpen, setClaimOpen] = useState(false);
  const [policyScanOpen, setPolicyScanOpen] = useState(false);
  const [policyScanConsent, setPolicyScanConsent] = useState(false);
  const [policyScanning, setPolicyScanning] = useState(false);
  const [policyScanError, setPolicyScanError] = useState("");
  const [policyScanFileName, setPolicyScanFileName] = useState("");
  const [policyScanReview, setPolicyScanReview] = useState(null);
  const policyFileRef = useRef(null);
  const policyCameraRef = useRef(null);
  const [policy, setPolicy] = useState(EMPTY_POLICY),
    [claim, setClaim] = useState(EMPTY_CLAIM);
  const [editingPolicy, setEditingPolicy] = useState(null),
    [editingClaim, setEditingClaim] = useState(null);
  const [placingRecord, setPlacingRecord] = useState(null);
  const [roomsDrawerOpen, setRoomsDrawerOpen] = useState(false);
  const [recordsDrawerOpen, setRecordsDrawerOpen] = useState(false);
  const [roomToRemove, setRoomToRemove] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const load = useCallback(async () => {
    try {
      const next = await propertyProfileApi.snapshot();
      setData(next);
      setProfile(next.profile || {});
    } catch (err) {
      setError(err.message || "Property context could not be loaded.");
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const run = async (action) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await action();
      await load();
      return true;
    } catch (err) {
      setError(err.message || "That change could not be saved.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const saveProfile = async (event) => {
    event.preventDefault();
    if (await run(() => propertyProfileApi.update(profile))) {
      setProfileOpen(false);
      setNotice("Property profile saved and shared context refreshed.");
    }
  };
  const applyAddress = (address) =>
    run(() =>
      propertyProfileApi.update({ ...profile, address_line1: address }),
    );
  const saveSpace = async (event) => {
    event.preventDefault();
    const normalizedName = String(space.name || "")
      .trim()
      .toLocaleLowerCase();
    const duplicate = spaces.some(
      (row) =>
        row.id !== editing &&
        String(row.name || "")
          .trim()
          .toLocaleLowerCase() === normalizedName,
    );
    if (duplicate) {
      setError("That room or space already exists. Choose another name.");
      return;
    }
    const ok = await run(() =>
      editing
        ? propertyProfileApi.updateSpace(editing, space)
        : propertyProfileApi.createSpace(space),
    );
    if (ok) {
      setEditing(false);
      setSpace(EMPTY_SPACE);
      setSpaceOpen(false);
      setNotice("Room saved.");
    }
  };
  const editSpace = (row) => {
    setError("");
    setEditing(row.id);
    setSpace({ ...EMPTY_SPACE, ...row });
    setSpaceOpen(true);
  };
  const removeRoom = async () => {
    if (!roomToRemove) return;
    const removedName = roomToRemove.name;
    const ok = await run(() => propertyProfileApi.deleteSpace(roomToRemove.id));
    if (ok) {
      setRoomToRemove(null);
      setSpaceOpen(false);
      setEditing(false);
      setSpace(EMPTY_SPACE);
      setNotice(
        `${removedName} removed. Connected records are now unassigned.`,
      );
    }
  };
  const setField = (key, value) =>
    setProfile((current) => ({ ...current, [key]: value }));
  const setSpaceField = (key, value) =>
    setSpace((current) => ({ ...current, [key]: value }));
  const savePolicy = async (event) => {
    event.preventDefault();
    const ok = await run(() =>
      editingPolicy
        ? propertyProfileApi.updateInsurance(editingPolicy, policy)
        : propertyProfileApi.createInsurance(policy),
    );
    if (ok) {
      setPolicyOpen(false);
      setEditingPolicy(null);
      setPolicy(EMPTY_POLICY);
      setPolicyScanReview(null);
      setNotice("Property coverage saved.");
    }
  };
  const openPolicyScanner = () => {
    setPolicyScanConsent(false);
    setPolicyScanError("");
    setPolicyScanFileName("");
    setPolicyScanOpen(true);
  };
  const scanPolicy = async (file) => {
    if (!file || !policyScanConsent || policyScanning) return;
    setPolicyScanning(true);
    setPolicyScanError("");
    setPolicyScanFileName(file.name || "Policy document");
    try {
      const result = await propertyProfileApi.scanInsurance(file);
      if (!result.ai_configured) {
        throw new Error(result.message || "Policy scanning is not configured.");
      }
      const extracted = result.policy || {};
      if (!String(extracted.provider_name || "").trim()) {
        throw new Error(
          "We could not identify an insurance provider. Try a clear image of the declarations page.",
        );
      }
      setEditingPolicy(null);
      setPolicy({ ...EMPTY_POLICY, ...extracted });
      setPolicyScanReview({
        confidence: result.confidence || "low",
        warnings: result.warnings || [],
        fileName: file.name || "Policy document",
      });
      setPolicyScanOpen(false);
      setPolicyOpen(true);
    } catch (err) {
      setPolicyScanError(err.message || "That policy could not be scanned.");
    } finally {
      setPolicyScanning(false);
      if (policyFileRef.current) policyFileRef.current.value = "";
      if (policyCameraRef.current) policyCameraRef.current.value = "";
    }
  };
  const saveClaim = async (event) => {
    event.preventDefault();
    const ok = await run(() =>
      editingClaim
        ? propertyProfileApi.updateClaim(editingClaim, claim)
        : propertyProfileApi.createClaim(claim),
    );
    if (ok) {
      setClaimOpen(false);
      setEditingClaim(null);
      setClaim(EMPTY_CLAIM);
      setNotice("Property claim saved to the household record.");
    }
  };
  const spaces = data?.spaces || [];
  const visibleSpaces = spaces.slice(0, 3);
  const connectedRecords = [
    ...(data?.equipment || []).map((row) => ({ ...row, kind: "vault" })),
    ...(data?.devices || []).map((row) => ({ ...row, kind: "device" })),
  ];
  const showSavingNotice = useMinLoad(busy && data !== null, 700);
  const connectedRecordRow = (row, inDrawer = false) => (
    <article key={`${row.kind}-${row.id}`}>
      <span>
        <strong>{row.name}</strong>
        <Link
          className="hm-property-source-link"
          to={row.kind === "vault" ? "/app/home-vault" : "/app/smart-home"}
        >
          {row.kind === "vault" ? "Home Vault" : "Smart Home"} · Open ›
        </Link>
      </span>
      <button
        className="hm-property-place"
        type="button"
        onClick={() => {
          if (inDrawer) setRecordsDrawerOpen(false);
          setPlacingRecord(row);
        }}
      >
        {spaces.find((spaceRow) => spaceRow.id === row.property_space_id)?.name || "Not placed"}
        <small>Change ›</small>
      </button>
    </article>
  );

  if (!data)
    return (
      <main className="hm-property-page">
        <div className="hm-property-loading">
          Connecting your property context…
        </div>
      </main>
    );
  return (
    <main className="hm-property-page">
      {PROFILE_DATALISTS.map(([id, options]) => (
        <datalist id={id} key={id}>
          {options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      ))}
      <section className="hm-property-hero">
        <div>
          <span>Property profile</span>
          <h1>Your home, mapped once</h1>
          <p>
            Rooms, systems, materials, and verified records share one address
            and location structure.
          </p>
        </div>
        <div className="hm-property-score">
          <strong>{data.completeness}%</strong>
          <span>complete</span>
        </div>
      </section>
      {error && <div className="hm-property-error">{error}</div>}
      {notice && (
        <div className="hm-property-notice" role="status">
          ✓ {notice}
        </div>
      )}
      {data.address_conflicts?.length > 0 && (
        <aside className="hm-property-warning">
          <strong>Address needs review</strong>
          <span>
            Connected records contain {data.address_conflicts.length} address{" "}
            {data.address_conflicts.length === 1 ? "difference" : "differences"}
            . Existing records will not be changed.
          </span>
          <div className="hm-property-warning-actions">
            {[
              ...new Map(
                data.address_conflicts.map((row) => [row.address, row]),
              ).values(),
            ].map((row) => (
              <button
                type="button"
                key={row.address}
                disabled={busy}
                onClick={() => applyAddress(row.address)}
              >
                Use "{row.address}" (
                {row.source_type === "VaultLease"
                  ? "lease"
                  : row.provider || "utility bill"}
                )
              </button>
            ))}
          </div>
        </aside>
      )}

      <section className="hm-property-card">
        <header>
          <div>
            <span>Home facts</span>
            <h2>{profile.display_name || "My household"}</h2>
          </div>
          <button
            className="hm-property-action"
            type="button"
            onClick={() => setProfileOpen(true)}
          >
            {data.completeness ? "Edit details" : "Set up property"}
          </button>
        </header>
        <div className="hm-property-facts">
          <div>
            <span>Address</span>
            <strong>
              {[profile.address_line1, profile.city, profile.state]
                .filter(Boolean)
                .join(", ") || "Not added"}
            </strong>
          </div>
          <div>
            <span>Home</span>
            <strong>
              {[
                profile.home_type,
                profile.year_built && `Built ${profile.year_built}`,
              ]
                .filter(Boolean)
                .join(" · ") || "Details not added"}
            </strong>
          </div>
          <div>
            <span>Systems</span>
            <strong>
              {[profile.heating_type, profile.cooling_type]
                .filter(Boolean)
                .join(" · ") || "Not added"}
            </strong>
          </div>
        </div>
        {profileOpen && (
          <div
            className="hm-property-sheet-bg"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setProfileOpen(false);
            }}
          >
            <form
              className="hm-property-sheet hm-property-form"
              onSubmit={saveProfile}
            >
              <SheetHandle
                onClose={() => setProfileOpen(false)}
                label="Close property form"
              />
              <header className="wide">
                <div>
                  <span>Property profile</span>
                  <h2>Edit home facts</h2>
                </div>
                <button type="button" onClick={() => setProfileOpen(false)}>
                  ×
                </button>
              </header>
              <label className="wide">
                Property name
                <input
                  value={profile.display_name || ""}
                  onChange={(e) => setField("display_name", e.target.value)}
                />
              </label>
              <label className="wide">
                Street address
                <input
                  value={profile.address_line1 || ""}
                  onChange={(e) => setField("address_line1", e.target.value)}
                />
              </label>
              <label>
                City
                <input
                  value={profile.city || ""}
                  onChange={(e) => setField("city", e.target.value)}
                />
              </label>
              <label>
                State
                <input
                  list="hm-pp-state"
                  maxLength="2"
                  value={profile.state || ""}
                  onChange={(e) =>
                    setField("state", e.target.value.toUpperCase())
                  }
                />
              </label>
              <label>
                ZIP / postal code
                <input
                  inputMode="numeric"
                  value={profile.postal_code || ""}
                  onChange={(e) => setField("postal_code", e.target.value)}
                />
              </label>
              <label>
                Country
                <select
                  value={profile.country || "US"}
                  onChange={(e) => setField("country", e.target.value)}
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="MX">Mexico</option>
                  <option value="GB">United Kingdom</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                Home type
                <input
                  list="hm-pp-home-type"
                  placeholder="Choose or type…"
                  value={profile.home_type || ""}
                  onChange={(e) => setField("home_type", e.target.value)}
                />
              </label>
              <label>
                Year built
                <input
                  type="number"
                  value={profile.year_built || ""}
                  onChange={(e) => setField("year_built", e.target.value)}
                />
              </label>
              <label>
                Interior sq ft
                <input
                  type="number"
                  min="0"
                  value={profile.interior_sq_ft || ""}
                  onChange={(e) => setField("interior_sq_ft", e.target.value)}
                />
              </label>
              <label>
                Lot sq ft
                <input
                  type="number"
                  min="0"
                  value={profile.lot_sq_ft || ""}
                  onChange={(e) => setField("lot_sq_ft", e.target.value)}
                />
              </label>
              <label>
                Stories
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={profile.stories || ""}
                  onChange={(e) => setField("stories", e.target.value)}
                />
              </label>
              <label>
                Bedrooms
                <input
                  type="number"
                  value={profile.bedrooms || ""}
                  onChange={(e) => setField("bedrooms", e.target.value)}
                />
              </label>
              <label>
                Bathrooms
                <input
                  type="number"
                  step="0.5"
                  value={profile.bathrooms || ""}
                  onChange={(e) => setField("bathrooms", e.target.value)}
                />
              </label>
              <label>
                Heating
                <input
                  list="hm-pp-heating-type"
                  value={profile.heating_type || ""}
                  onChange={(e) => setField("heating_type", e.target.value)}
                />
              </label>
              <label>
                Cooling
                <input
                  list="hm-pp-cooling-type"
                  value={profile.cooling_type || ""}
                  onChange={(e) => setField("cooling_type", e.target.value)}
                />
              </label>
              <label>
                Foundation
                <input
                  list="hm-pp-foundation-type"
                  value={profile.foundation_type || ""}
                  onChange={(e) => setField("foundation_type", e.target.value)}
                />
              </label>
              <label>
                Roof
                <input
                  list="hm-pp-roof-type"
                  value={profile.roof_type || ""}
                  onChange={(e) => setField("roof_type", e.target.value)}
                />
              </label>
              <label>
                Exterior material
                <input
                  list="hm-pp-exterior-material"
                  value={profile.exterior_material || ""}
                  onChange={(e) =>
                    setField("exterior_material", e.target.value)
                  }
                />
              </label>
              <label>
                Water source
                <input
                  list="hm-pp-water-source"
                  value={profile.water_source || ""}
                  onChange={(e) => setField("water_source", e.target.value)}
                />
              </label>
              <label>
                Sewer / septic
                <input
                  list="hm-pp-sewer-type"
                  value={profile.sewer_type || ""}
                  onChange={(e) => setField("sewer_type", e.target.value)}
                />
              </label>
              <label className="wide">
                Property notes
                <input
                  value={profile.notes || ""}
                  placeholder="Gate codes, access notes, or useful context"
                  onChange={(e) => setField("notes", e.target.value)}
                />
              </label>
              <div className="hm-property-form__actions wide">
                <button
                  type="button"
                  className="quiet"
                  onClick={() => setProfileOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" disabled={busy}>
                  {busy ? "Saving…" : "Save property profile"}
                </button>
              </div>
            </form>
          </div>
        )}
      </section>

      <section className="hm-property-card">
        <header>
          <div>
            <span>Coverage & claims</span>
            <h2>Protect this property</h2>
          </div>
          <div className="hm-property-header-actions">
            <button
              className="hm-property-action hm-property-scan-action"
              type="button"
              onClick={openPolicyScanner}
            >
              📄 Scan home policy
            </button>
            <button
              className="hm-property-action"
              type="button"
              onClick={() => {
                setEditingPolicy(null);
                setPolicy(EMPTY_POLICY);
                setPolicyScanReview(null);
                setPolicyOpen(true);
              }}
            >
              + Add manually
            </button>
          </div>
        </header>
        <div className="hm-property-spaces">
          {(data.insurance || []).map((row) => (
            <article key={row.id}>
              <div>
                <strong>{row.provider_name}</strong>
                <span>
                  {row.coverage_type} ·{" "}
                  {row.policy_expiry
                    ? `Renews ${row.policy_expiry}`
                    : "Renewal not set"}
                </span>
              </div>
              <small>
                {row.monthly_premium != null
                  ? `$${row.monthly_premium.toFixed(2)}/mo`
                  : "Premium not added"}
              </small>
              <button
                type="button"
                onClick={() => {
                  setEditingPolicy(row.id);
                  setPolicy({ ...EMPTY_POLICY, ...row });
                  setPolicyScanReview(null);
                  setPolicyOpen(true);
                }}
              >
                Edit
              </button>
            </article>
          ))}
        </div>
        <div className="hm-property-links">
          {(data.claims || []).map((row) => (
            <article key={row.id}>
              <span>
                <strong>{row.description}</strong>
                <small>
                  {row.incident_date} · {row.status}
                </small>
              </span>
              <em>
                {row.amount_claimed != null
                  ? `$${row.amount_claimed.toLocaleString()}`
                  : "Amount pending"}
              </em>
              <button
                type="button"
                onClick={() => {
                  setEditingClaim(row.id);
                  setClaim({ ...EMPTY_CLAIM, ...row });
                  setClaimOpen(true);
                }}
              >
                Edit
              </button>
            </article>
          ))}
        </div>
        <button
          className="hm-property-action"
          type="button"
          onClick={() => {
            setEditingClaim(null);
            setClaim({
              ...EMPTY_CLAIM,
              policy_id: data.insurance?.[0]?.id || "",
            });
            setClaimOpen(true);
          }}
        >
          + Record claim
        </button>
        {policyScanOpen && (
          <div
            className="hm-property-sheet-bg"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !policyScanning) {
                setPolicyScanOpen(false);
              }
            }}
          >
            <section className="hm-property-sheet hm-policy-scan-sheet">
              <SheetHandle
                onClose={() => !policyScanning && setPolicyScanOpen(false)}
                label="Close policy scanner"
              />
              <header>
                <div>
                  <span>Secure document scan</span>
                  <h2>Scan your home policy</h2>
                </div>
                <button
                  type="button"
                  disabled={policyScanning}
                  onClick={() => setPolicyScanOpen(false)}
                >
                  ×
                </button>
              </header>
              <div className="hm-policy-scan-intro">
                <span aria-hidden="true">🏠🛡️</span>
                <div>
                  <strong>Start with the declarations page</strong>
                  <p>
                    HouseMind can read a clear photo or PDF and prefill your
                    provider, policy number, coverage, deductible, and dates.
                  </p>
                </div>
              </div>
              <label className="hm-policy-scan-consent">
                <input
                  type="checkbox"
                  checked={policyScanConsent}
                  disabled={policyScanning}
                  onChange={(event) =>
                    setPolicyScanConsent(event.target.checked)
                  }
                />
                <span>
                  I consent to AI reading this document to extract policy
                  details. The uploaded file is not saved by HouseMind.
                </span>
              </label>
              {policyScanError && (
                <div className="hm-property-sheet-error" role="alert">
                  {policyScanError}
                </div>
              )}
              {policyScanning ? (
                <div className="hm-policy-scanning" role="status">
                  <span className="hm-policy-scanning-dot" />
                  <div>
                    <strong>Reading {policyScanFileName}…</strong>
                    <small>Finding the declarations and coverage details</small>
                  </div>
                </div>
              ) : (
                <div className="hm-policy-scan-choices">
                  <button
                    type="button"
                    disabled={!policyScanConsent}
                    onClick={() => policyCameraRef.current?.click()}
                  >
                    <span>📷</span>
                    Take a photo
                  </button>
                  <button
                    type="button"
                    disabled={!policyScanConsent}
                    onClick={() => policyFileRef.current?.click()}
                  >
                    <span>📎</span>
                    Upload image or PDF
                  </button>
                </div>
              )}
              <input
                ref={policyCameraRef}
                className="hm-policy-file-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event) => scanPolicy(event.target.files?.[0])}
              />
              <input
                ref={policyFileRef}
                className="hm-policy-file-input"
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={(event) => scanPolicy(event.target.files?.[0])}
              />
              <p className="hm-policy-scan-privacy">
                Review and edit every extracted field before anything is saved.
              </p>
            </section>
          </div>
        )}
        {policyOpen && (
          <div
            className="hm-property-sheet-bg"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setPolicyOpen(false);
            }}
          >
            <form
              className="hm-property-sheet hm-property-form"
              onSubmit={savePolicy}
            >
              <SheetHandle
                onClose={() => setPolicyOpen(false)}
                label="Close policy form"
              />
              <header className="wide">
                <div>
                  <span>Property coverage</span>
                  <h2>
                    {editingPolicy ? "Edit policy" : "Add insurance policy"}
                  </h2>
                </div>
                <button type="button" onClick={() => setPolicyOpen(false)}>
                  ×
                </button>
              </header>
              {policyScanReview && (
                <aside className="hm-policy-scan-review wide">
                  <div>
                    <span aria-hidden="true">✨</span>
                    <p>
                      <strong>Scan ready to review</strong>
                      Check every field below against {policyScanReview.fileName}
                      before saving.
                    </p>
                    <em className={`is-${policyScanReview.confidence}`}>
                      {policyScanReview.confidence} confidence
                    </em>
                  </div>
                  {policyScanReview.warnings.length > 0 && (
                    <ul>
                      {policyScanReview.warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  )}
                </aside>
              )}
              <label>
                Provider
                <input
                  required
                  value={policy.provider_name || ""}
                  onChange={(e) =>
                    setPolicy((v) => ({ ...v, provider_name: e.target.value }))
                  }
                />
              </label>
              <label>
                Policy number
                <input
                  value={policy.policy_number || ""}
                  onChange={(e) =>
                    setPolicy((v) => ({ ...v, policy_number: e.target.value }))
                  }
                />
              </label>
              <label>
                Coverage type
                <select
                  value={policy.coverage_type || "homeowner"}
                  onChange={(e) =>
                    setPolicy((v) => ({ ...v, coverage_type: e.target.value }))
                  }
                >
                  <option value="homeowner">Homeowner</option>
                  <option value="renters">Renters</option>
                  <option value="condo">Condo</option>
                  <option value="landlord">Landlord</option>
                  <option value="flood">Flood</option>
                  <option value="earthquake">Earthquake</option>
                </select>
              </label>
              <label>
                Monthly premium
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={policy.monthly_premium ?? ""}
                  onChange={(e) =>
                    setPolicy((v) => ({
                      ...v,
                      monthly_premium: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Deductible
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={policy.deductible ?? ""}
                  onChange={(e) =>
                    setPolicy((v) => ({ ...v, deductible: e.target.value }))
                  }
                />
              </label>
              <label>
                Dwelling coverage
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={policy.dwelling_coverage ?? ""}
                  onChange={(e) =>
                    setPolicy((v) => ({
                      ...v,
                      dwelling_coverage: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Personal property
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={policy.personal_property_coverage ?? ""}
                  onChange={(e) =>
                    setPolicy((v) => ({
                      ...v,
                      personal_property_coverage: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Liability coverage
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={policy.liability_coverage ?? ""}
                  onChange={(e) =>
                    setPolicy((v) => ({
                      ...v,
                      liability_coverage: e.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Effective date
                <input
                  type="date"
                  value={policy.effective_date || ""}
                  onChange={(e) =>
                    setPolicy((v) => ({ ...v, effective_date: e.target.value }))
                  }
                />
              </label>
              <label>
                Renewal / expiry
                <input
                  type="date"
                  value={policy.policy_expiry || ""}
                  onChange={(e) =>
                    setPolicy((v) => ({ ...v, policy_expiry: e.target.value }))
                  }
                />
              </label>
              <label className="wide">
                Agent / contact
                <input
                  value={policy.agent_contact || ""}
                  onChange={(e) =>
                    setPolicy((v) => ({ ...v, agent_contact: e.target.value }))
                  }
                />
              </label>
              <label className="wide">
                Notes
                <textarea
                  rows="2"
                  value={policy.notes || ""}
                  onChange={(e) =>
                    setPolicy((v) => ({ ...v, notes: e.target.value }))
                  }
                />
              </label>
              <div className="hm-property-form__actions wide">
                <button
                  type="button"
                  className="quiet"
                  onClick={() => setPolicyOpen(false)}
                >
                  Cancel
                </button>
                <button disabled={busy}>
                  {busy ? "Saving…" : "Save policy"}
                </button>
              </div>
            </form>
          </div>
        )}
        {claimOpen && (
          <div
            className="hm-property-sheet-bg"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setClaimOpen(false);
            }}
          >
            <form
              className="hm-property-sheet hm-property-form"
              onSubmit={saveClaim}
            >
              <SheetHandle
                onClose={() => setClaimOpen(false)}
                label="Close claim form"
              />
              <header className="wide">
                <div>
                  <span>Property claim</span>
                  <h2>{editingClaim ? "Update claim" : "Record a claim"}</h2>
                </div>
                <button type="button" onClick={() => setClaimOpen(false)}>
                  ×
                </button>
              </header>
              <label>
                Policy
                <select
                  value={claim.policy_id || ""}
                  onChange={(e) =>
                    setClaim((v) => ({ ...v, policy_id: e.target.value }))
                  }
                >
                  <option value="">No policy linked</option>
                  {(data.insurance || []).map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.provider_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Incident date
                <input
                  required
                  type="date"
                  value={claim.incident_date || ""}
                  onChange={(e) =>
                    setClaim((v) => ({ ...v, incident_date: e.target.value }))
                  }
                />
              </label>
              <label>
                Claim type
                <select
                  value={claim.claim_type || "property_damage"}
                  onChange={(e) =>
                    setClaim((v) => ({ ...v, claim_type: e.target.value }))
                  }
                >
                  <option value="property_damage">Property damage</option>
                  <option value="water">Water</option>
                  <option value="fire">Fire</option>
                  <option value="theft">Theft</option>
                  <option value="liability">Liability</option>
                  <option value="weather">Weather</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                Status
                <select
                  value={claim.status || "open"}
                  onChange={(e) =>
                    setClaim((v) => ({ ...v, status: e.target.value }))
                  }
                >
                  <option value="open">Open</option>
                  <option value="submitted">Submitted</option>
                  <option value="reviewing">Under review</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                  <option value="denied">Denied</option>
                  <option value="closed">Closed</option>
                </select>
              </label>
              <label className="wide">
                What happened?
                <textarea
                  required
                  rows="2"
                  value={claim.description || ""}
                  onChange={(e) =>
                    setClaim((v) => ({ ...v, description: e.target.value }))
                  }
                />
              </label>
              <label>
                Claim number
                <input
                  value={claim.claim_number || ""}
                  onChange={(e) =>
                    setClaim((v) => ({ ...v, claim_number: e.target.value }))
                  }
                />
              </label>
              <label>
                Amount claimed
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={claim.amount_claimed ?? ""}
                  onChange={(e) =>
                    setClaim((v) => ({ ...v, amount_claimed: e.target.value }))
                  }
                />
              </label>
              <label>
                Amount paid
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={claim.amount_paid ?? ""}
                  onChange={(e) =>
                    setClaim((v) => ({ ...v, amount_paid: e.target.value }))
                  }
                />
              </label>
              <div className="hm-property-form__actions wide">
                <button
                  type="button"
                  className="quiet"
                  onClick={() => setClaimOpen(false)}
                >
                  Cancel
                </button>
                <button disabled={busy}>
                  {busy ? "Saving…" : "Save claim"}
                </button>
              </div>
            </form>
          </div>
        )}
      </section>

      <section className="hm-property-card">
        <header>
          <div>
            <span>Rooms & spaces</span>
            <h2>
              {spaces.length
                ? `${spaces.length} mapped spaces`
                : "Start your home map"}
            </h2>
          </div>
          <button
            className="hm-property-action"
            type="button"
            onClick={() => {
              setError("");
              setEditing(false);
              setSpace(EMPTY_SPACE);
              setSpaceOpen(true);
            }}
          >
            + Add room
          </button>
        </header>
        <div className="hm-property-spaces">
          {visibleSpaces.map((row) => (
            <article key={row.id}>
              <div>
                <strong>{row.name}</strong>
                <span>
                  {[row.space_type, row.floor_level]
                    .filter(Boolean)
                    .join(" · ") || "Household space"}
                </span>
              </div>
              <small>
                {[row.paint_color, row.flooring].filter(Boolean).join(" · ") ||
                  "Add materials and safety notes"}
              </small>
              <button type="button" onClick={() => editSpace(row)}>
                Edit
              </button>
            </article>
          ))}
        </div>
        {spaces.length > 3 && (
          <button
            className="hm-property-view-all"
            type="button"
            onClick={() => setRoomsDrawerOpen(true)}
          >
            View all {spaces.length} rooms ›
          </button>
        )}
        {spaceOpen && (
          <div
            className="hm-property-sheet-bg"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setSpaceOpen(false);
            }}
          >
            <form
              className="hm-property-sheet hm-property-form hm-property-space-form"
              onSubmit={saveSpace}
            >
              <SheetHandle
                onClose={() => setSpaceOpen(false)}
                label="Close room form"
              />
              <header className="wide">
                <div>
                  <span>Property map</span>
                  <h2>{editing ? "Edit space" : "Add a room or space"}</h2>
                </div>
                <button type="button" onClick={() => setSpaceOpen(false)}>
                  ×
                </button>
              </header>
              {error && (
                <div className="hm-property-sheet-error wide">{error}</div>
              )}
              <label>
                Space name
                <input
                  required
                  value={space.name}
                  placeholder="Kitchen"
                  onChange={(e) => setSpaceField("name", e.target.value)}
                />
              </label>
              <label>
                Type
                <select
                  value={space.space_type}
                  onChange={(e) => setSpaceField("space_type", e.target.value)}
                >
                  <option value="room">Room</option>
                  <option value="garage">Garage</option>
                  <option value="yard">Yard</option>
                  <option value="utility">Utility area</option>
                  <option value="exterior">Exterior</option>
                </select>
              </label>
              <label>
                Floor / level
                <input
                  list="hm-pp-floor-level"
                  value={space.floor_level || ""}
                  placeholder="Main floor"
                  onChange={(e) => setSpaceField("floor_level", e.target.value)}
                />
              </label>
              <label>
                Flooring
                <input
                  list="hm-pp-flooring"
                  value={space.flooring || ""}
                  onChange={(e) => setSpaceField("flooring", e.target.value)}
                />
              </label>
              <label>
                Paint color
                <input
                  value={space.paint_color || ""}
                  onChange={(e) => setSpaceField("paint_color", e.target.value)}
                />
              </label>
              <label>
                Safety / shutoff note
                <input
                  value={space.safety_notes || ""}
                  onChange={(e) =>
                    setSpaceField("safety_notes", e.target.value)
                  }
                />
              </label>
              <div
                className={`hm-property-form__actions wide${editing ? " is-editing-room" : ""}`}
              >
                <button
                  type="button"
                  className="quiet"
                  onClick={() => setSpaceOpen(false)}
                >
                  Cancel
                </button>
                {editing && (
                  <button
                    className="hm-property-remove-room"
                    type="button"
                    onClick={() =>
                      setRoomToRemove({ id: editing, name: space.name })
                    }
                  >
                    Remove room
                  </button>
                )}
                <button disabled={busy}>
                  {editing ? "Save changes" : "Add space"}
                </button>
              </div>
            </form>
          </div>
        )}
      </section>

      {roomsDrawerOpen && (
        <div
          className="hm-property-sheet-bg"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setRoomsDrawerOpen(false);
          }}
        >
          <section className="hm-property-record-drawer">
            <SheetHandle
              onClose={() => setRoomsDrawerOpen(false)}
              label="Close rooms list"
            />
            <header>
              <div>
                <span>Rooms & spaces</span>
                <h2>All {spaces.length} mapped spaces</h2>
              </div>
              <button type="button" onClick={() => setRoomsDrawerOpen(false)}>
                ×
              </button>
            </header>
            <div className="hm-property-spaces">
              {spaces.map((row) => (
                <article key={row.id}>
                  <div>
                    <strong>{row.name}</strong>
                    <span>
                      {[row.space_type, row.floor_level]
                        .filter(Boolean)
                        .join(" · ") || "Household space"}
                    </span>
                  </div>
                  <small>
                    {[row.paint_color, row.flooring]
                      .filter(Boolean)
                      .join(" · ") || "Add materials and safety notes"}
                  </small>
                  <button
                    type="button"
                    onClick={() => {
                      setRoomsDrawerOpen(false);
                      editSpace(row);
                    }}
                  >
                    Edit
                  </button>
                </article>
              ))}
            </div>
          </section>
        </div>
      )}

      <section className="hm-property-card">
        <header>
          <div>
            <span>Connected records</span>
            <h2>Put equipment where it belongs</h2>
          </div>
          <em>{data.summary.equipment + data.summary.devices} records</em>
        </header>
        <div className="hm-property-links">
          {connectedRecords.slice(0, 3).map((row) => connectedRecordRow(row))}
          {!data.summary.equipment && !data.summary.devices && (
            <div className="hm-property-empty">Add equipment or devices to organize them by room.</div>
          )}
        </div>
        {connectedRecords.length > 3 && (
          <button className="hm-property-view-all" type="button" onClick={() => setRecordsDrawerOpen(true)}>
            View all {connectedRecords.length} connected records ›
          </button>
        )}
      </section>
      {recordsDrawerOpen && (
        <div className="hm-property-sheet-bg" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setRecordsDrawerOpen(false);
        }}>
          <section className="hm-property-record-drawer">
            <SheetHandle onClose={() => setRecordsDrawerOpen(false)} label="Close connected records" />
            <header>
              <div><span>Connected records</span><h2>All {connectedRecords.length} records</h2></div>
              <button type="button" onClick={() => setRecordsDrawerOpen(false)}>×</button>
            </header>
            <div className="hm-property-links">
              {connectedRecords.map((row) => connectedRecordRow(row, true))}
            </div>
          </section>
        </div>
      )}
      {placingRecord && (
        <div
          className="hm-property-sheet-bg"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPlacingRecord(null);
          }}
        >
          <form
            className="hm-property-sheet hm-property-form hm-property-place-sheet"
            onSubmit={async (event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              const spaceId = formData.get("property_space_id") || "";
              const ok = await run(() =>
                placingRecord.kind === "vault"
                  ? propertyProfileApi.linkVault(placingRecord.id, spaceId)
                  : propertyProfileApi.linkDevice(placingRecord.id, spaceId),
              );
              if (ok) {
                setPlacingRecord(null);
                setNotice("Record location updated across HouseMind.");
              }
            }}
          >
            <SheetHandle
              onClose={() => setPlacingRecord(null)}
              label="Close location form"
            />
            <header className="wide">
              <div>
                <span>Connected record</span>
                <h2>Place {placingRecord.name}</h2>
              </div>
              <button type="button" onClick={() => setPlacingRecord(null)}>
                ×
              </button>
            </header>
            <label className="wide">
              Room or space
              <select
                name="property_space_id"
                defaultValue={placingRecord.property_space_id || ""}
              >
                <option value="">Not placed</option>
                {spaces.map((spaceRow) => (
                  <option value={spaceRow.id} key={spaceRow.id}>
                    {spaceRow.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="hm-property-form__actions wide">
              <button
                type="button"
                className="quiet"
                onClick={() => setPlacingRecord(null)}
              >
                Cancel
              </button>
              <button type="submit" disabled={busy}>
                {busy ? "Saving…" : "Save location"}
              </button>
            </div>
          </form>
        </div>
      )}
      {roomToRemove && (
        <ConfirmModal
          tag="⚠ Remove room"
          title={`Remove ${roomToRemove.name}?`}
          message="The room will be removed from Property Profile. Connected equipment, Smart Home devices, and Home Care tasks will be kept, but their room assignment will be cleared."
          confirmLabel="Remove room"
          onCancel={() => setRoomToRemove(null)}
          onConfirm={removeRoom}
          loading={busy}
        />
      )}
      <ModuleAskBar
        module="property_profile"
        label="Property Profile"
        placeholder="Ask about your property…"
      />
      {showSavingNotice && <SavingNotice message="Saving property changes..." />}
    </main>
  );
}
